import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  dateOnlyStringToCalendarDate,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useCreateContractFeeScheduleMutation,
  useCreateBulkContractFeeScheduleMutation,
  useUploadAttachmentMutation,
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useLazyGetContractFeeSchedulesQuery,
  RateCalculation,
  RateType,
  normalizeRateType,
  ICarveout,
} from "store/queries/contractFeeSchedules";
import { useGetAllExplodingFeeSchedulesQuery } from "store/queries/explodingFeeSchedule";
import { useGetInpatientRulesetsQuery } from "store/queries/inpatientRulesets";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";

import {
  buildRulesetOptionGroups,
  rulesetGroupTemplate,
} from "./rulesetOptions";

interface IFormData {
  providerIds: string[];
  payerIds: number[];
  effectiveDate: Date | null;
  nextUpdateDate: Date | null;
  rateCalculations: RateCalculation[];
  attachmentFiles: File[];
  mpdrRulesetIds: string[];
  caseRateRulesetIds: string[];
  explodingFeeScheduleRulesetIds: string[];
  packageRulesetIds: string[];
  inpatientRulesetIds: string[];
  medicareAdvantagePayerIds: number[];
  notes?: string;
}

export function CreateContractFeeSchedulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: hospitalId } = useParams();
  const copyFromId = searchParams.get("copyFrom");
  const accountId = searchParams.get("accountId") || hospitalId;
  const hospitalOrAccountForContractLinks =
    hospitalId || accountId || undefined;

  useDocumentTitle(
    accountId
      ? "Create Contract Fee Schedule (Account Context)"
      : "Create Contract Fee Schedule"
  );
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  // Form state
  const [formData, setFormData] = useState<IFormData>({
    providerIds: [],
    payerIds: [],
    effectiveDate: null,
    nextUpdateDate: null,
    rateCalculations: [],
    attachmentFiles: [],
    mpdrRulesetIds: [],
    caseRateRulesetIds: [],
    explodingFeeScheduleRulesetIds: [],
    packageRulesetIds: [],
    inpatientRulesetIds: [],
    medicareAdvantagePayerIds: [],
    notes: "",
  });

  // New rate calculation state
  const [newRateCalc, setNewRateCalc] = useState<{
    baseRateFileId: string;
    ratePercent: number;
    rateType: RateType;
  }>({
    baseRateFileId: "",
    ratePercent: 100,
    rateType: "percent",
  });

  /** Library carveout BaseRateFile ids — same queue pattern as Edit Contract. */
  const [pendingCarveoutBaseRateFileIds, setPendingCarveoutBaseRateFileIds] =
    useState<string[]>([]);
  const [selectedCarveoutBaseRateFileId, setSelectedCarveoutBaseRateFileId] =
    useState<string | null>(null);

  // Data fetching
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({
    accountId: accountId || undefined,
  });
  const { data: mpdrRulesets } = useGetAllDiscountRulesetsQuery();
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery();
  const { data: packageRulesetsData } = useGetAllPackageRulesetsQuery();
  const { data: inpatientRulesetsData } = useGetInpatientRulesetsQuery();
  const { data: explodingFeeSchedulesData } =
    useGetAllExplodingFeeSchedulesQuery();
  const [createContract] = useCreateContractFeeScheduleMutation();
  const [createBulkContract] = useCreateBulkContractFeeScheduleMutation();
  const [uploadAttachment] = useUploadAttachmentMutation();

  // Fetch source contract if copying
  const { data: sourceContract } = useGetContractFeeScheduleQuery(
    copyFromId ?? "",
    {
      skip: !copyFromId,
    }
  );

  const singleProviderIdForDuplicateCheck =
    formData.providerIds.length === 1 ? formData.providerIds[0] : undefined;
  const singlePayerIdForDuplicateCheck =
    formData.payerIds.length === 1 ? formData.payerIds[0] : undefined;

  const {
    data: existingContractsForPair = [],
    refetch: refetchExistingContractPair,
  } = useGetContractFeeSchedulesQuery(
    {
      providerId: singleProviderIdForDuplicateCheck ?? "",
      payerId: singlePayerIdForDuplicateCheck ?? 0,
      ...(accountId ? { accountId } : {}),
    },
    {
      skip:
        !singleProviderIdForDuplicateCheck ||
        singlePayerIdForDuplicateCheck == null,
    }
  );

  const [lookupContractsForPair] = useLazyGetContractFeeSchedulesQuery();

  // Track selected providers (for backwards compat when rateType is missing)
  const selectedProviders =
    providers?.data?.filter((p: any) =>
      (formData.providerIds ?? []).includes(p.id)
    ) || [];

  // Track selected payers
  const selectedPayers =
    payers?.data?.filter((p: any) =>
      (formData.payerIds ?? []).includes(p.id)
    ) || [];
  // Check if any selected payer is Medicare
  const hasMedicarePayer = selectedPayers.some(
    (p: any) => p.type === "OriginalMedicare"
  );

  // Clear Medicare Advantage payer IDs when no Medicare payer is selected
  useEffect(() => {
    if (!hasMedicarePayer && formData.medicareAdvantagePayerIds.length > 0) {
      setFormData((prev) => ({
        ...prev,
        medicareAdvantagePayerIds: [],
      }));
    }
  }, [hasMedicarePayer]);

  // Loading state for submit button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if we've already populated the form from the source contract
  const [hasPopulatedFromSource, setHasPopulatedFromSource] = useState(false);

  // Track whether to copy carveouts when copying a contract
  const [copyCarveouts, setCopyCarveouts] = useState(true);

  // Track existing rulesets for display purposes (to prevent duplicate creation)
  // These will be automatically linked when contract is created
  const [existingMpdrRulesets, setExistingMpdrRulesets] = useState<any[]>([]);
  const [existingCaseRateRulesets, setExistingCaseRateRulesets] = useState<
    any[]
  >([]);
  const [existingPackageRulesets, setExistingPackageRulesets] = useState<any[]>(
    []
  );
  const [existingInpatientRulesets, setExistingInpatientRulesets] = useState<
    any[]
  >([]);
  const [existingExplodingFeeSchedules, setExistingExplodingFeeSchedules] =
    useState<any[]>([]);

  // Populate form data when copying from another contract
  useEffect(() => {
    if (sourceContract?.data && copyFromId && !hasPopulatedFromSource) {
      const source = sourceContract.data;
      setFormData({
        providerIds: [source.providerId],
        payerIds: [source.payerId],
        effectiveDate: dateOnlyStringToCalendarDate(source.effectiveDate),
        nextUpdateDate: source.nextUpdateDate
          ? dateOnlyStringToCalendarDate(source.nextUpdateDate)
          : null,
        rateCalculations: (source.rateCalculations || []).map((calc) => ({
          ...calc,
          ratePercent: Number(calc.ratePercent),
          rateType: normalizeRateType(calc.rateType),
        })),
        attachmentFiles: [], // Don't copy files
        mpdrRulesetIds: source.mpdrRulesetIds || [],
        caseRateRulesetIds: source.caseRateRulesetIds || [],
        explodingFeeScheduleRulesetIds:
          source.explodingFeeScheduleRulesetIds || [],
        packageRulesetIds: source.packageRulesetIds || [],
        inpatientRulesetIds: source.inpatientRulesetIds || [],
        medicareAdvantagePayerIds:
          source.medicareAdvantagePlans?.map(
            (link: any) => link.medicareAdvantagePlanId
          ) || [],
      });
      setHasPopulatedFromSource(true);
    }
  }, [sourceContract, copyFromId]);

  // Clean up deleted ruleset IDs from form data
  useEffect(() => {
    if (mpdrRulesets?.data && formData.mpdrRulesetIds.length > 0) {
      const availableIds = mpdrRulesets.data.map((r: any) => r.id);
      const validIds = formData.mpdrRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.mpdrRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          mpdrRulesetIds: validIds,
        }));
      }
    }
  }, [mpdrRulesets?.data, formData.mpdrRulesetIds]);

  useEffect(() => {
    if (caseRateRulesets?.data && formData.caseRateRulesetIds.length > 0) {
      const availableIds = caseRateRulesets.data.map((r: any) => r.id);
      const validIds = formData.caseRateRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.caseRateRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          caseRateRulesetIds: validIds,
        }));
      }
    }
  }, [caseRateRulesets?.data, formData.caseRateRulesetIds]);

  useEffect(() => {
    if (
      explodingFeeSchedulesData?.data &&
      formData.explodingFeeScheduleRulesetIds.length > 0
    ) {
      const availableIds = explodingFeeSchedulesData.data.map((r: any) => r.id);
      const validIds = formData.explodingFeeScheduleRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.explodingFeeScheduleRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          explodingFeeScheduleRulesetIds: validIds,
        }));
      }
    }
  }, [
    explodingFeeSchedulesData?.data,
    formData.explodingFeeScheduleRulesetIds,
  ]);

  useEffect(() => {
    if (packageRulesetsData?.data && formData.packageRulesetIds.length > 0) {
      const availableIds = packageRulesetsData.data.map((r: any) => r.id);
      const validIds = formData.packageRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.packageRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          packageRulesetIds: validIds,
        }));
      }
    }
  }, [packageRulesetsData?.data, formData.packageRulesetIds]);

  useEffect(() => {
    if (
      inpatientRulesetsData?.data &&
      formData.inpatientRulesetIds.length > 0
    ) {
      const availableIds = inpatientRulesetsData.data.map((r: any) => r.id);
      const validIds = formData.inpatientRulesetIds.filter((id) =>
        availableIds.includes(id)
      );
      if (validIds.length !== formData.inpatientRulesetIds.length) {
        setFormData((prev) => ({
          ...prev,
          inpatientRulesetIds: validIds,
        }));
      }
    }
  }, [inpatientRulesetsData?.data, formData.inpatientRulesetIds]);

  // Fetch existing rulesets when provider/payer changes (for display only)
  useEffect(() => {
    // Only show existing rulesets when single provider and single payer are selected
    if (formData.providerIds.length === 1 && formData.payerIds.length === 1) {
      const providerId = formData.providerIds[0];
      const payerId = formData.payerIds[0];
      // Filter existing MPDR rulesets for this provider/payer combination
      const filteredMpdrRulesets =
        mpdrRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingMpdrRulesets(filteredMpdrRulesets);

      // Filter existing case rate rulesets for this provider/payer combination
      const filteredCaseRateRulesets =
        caseRateRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingCaseRateRulesets(filteredCaseRateRulesets);

      // Filter existing package rulesets for this provider/payer combination
      const filteredPackageRulesets =
        packageRulesetsData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingPackageRulesets(filteredPackageRulesets);

      // Filter existing inpatient rulesets for this provider/payer combination
      const filteredInpatientRulesets =
        inpatientRulesetsData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingInpatientRulesets(filteredInpatientRulesets);

      // Filter existing exploding fee schedule rulesets for this provider/payer combination
      const filteredExplodingFeeSchedules =
        explodingFeeSchedulesData?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === providerId && ruleset.payer?.id === payerId
        ) || [];
      setExistingExplodingFeeSchedules(filteredExplodingFeeSchedules);
    } else {
      setExistingMpdrRulesets([]);
      setExistingCaseRateRulesets([]);
      setExistingPackageRulesets([]);
      setExistingInpatientRulesets([]);
      setExistingExplodingFeeSchedules([]);
    }
  }, [
    formData.providerIds,
    formData.payerIds,
    mpdrRulesets?.data,
    caseRateRulesets?.data,
    packageRulesetsData?.data,
    inpatientRulesetsData?.data,
    explodingFeeSchedulesData?.data,
  ]);

  // Options for dropdowns
  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];

    // If we're in account context, filter providers to only show those from this account
    let filteredProviders = providers.data;
    if (accountId) {
      filteredProviders = providers.data.filter(
        (p: any) => p.accountId === accountId
      );
    }

    return filteredProviders.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers, accountId]);

  const payerOptions = useMemo(() => {
    return (
      payers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  const baseRateFileOptions = useMemo(() => {
    if (!baseRateFiles?.data) return [];

    // If we're in account context, filter base rate files
    let filteredFiles = baseRateFiles.data.filter((f) => f.isCarveout !== true);
    if (accountId) {
      filteredFiles = filteredFiles.filter(
        (f) =>
          // Include files with no accountId (super admin files) or matching accountId
          !f.accountId || f.accountId === accountId
      );
    }

    return filteredFiles.map((f) => ({
      label: f.displayName,
      value: f.id,
    }));
  }, [baseRateFiles, accountId]);

  const carveoutBaseRateFileOptions = useMemo(() => {
    if (!baseRateFiles?.data) return [];
    let filteredFiles = baseRateFiles.data.filter((f) => f.isCarveout === true);
    if (accountId) {
      filteredFiles = filteredFiles.filter(
        (f) => !f.accountId || f.accountId === accountId
      );
    }
    return filteredFiles
      .filter((f) => !pendingCarveoutBaseRateFileIds.includes(f.id))
      .map((f) => ({
        label: `${f.displayName} (v${f.version})`,
        value: f.id,
      }));
  }, [baseRateFiles, accountId, pendingCarveoutBaseRateFileIds]);

  const mpdrRulesetOptions = useMemo(
    () =>
      buildRulesetOptionGroups(
        mpdrRulesets?.data,
        "groupName",
        "Unnamed Ruleset"
      ),
    [mpdrRulesets]
  );

  const caseRateRulesetOptions = useMemo(
    () =>
      buildRulesetOptionGroups(
        caseRateRulesets?.data,
        "groupName",
        "Unnamed Ruleset"
      ),
    [caseRateRulesets]
  );

  const explodingRulesetOptions = useMemo(
    () =>
      buildRulesetOptionGroups(
        explodingFeeSchedulesData?.data,
        "groupName",
        "Unnamed Exploding Fee Schedule"
      ),
    [explodingFeeSchedulesData]
  );

  const packageRulesetOptions = useMemo(
    () =>
      buildRulesetOptionGroups(
        packageRulesetsData?.data,
        "groupName",
        "Unnamed Package Ruleset"
      ),
    [packageRulesetsData]
  );

  const inpatientRulesetOptions = useMemo(
    () =>
      buildRulesetOptionGroups(
        inpatientRulesetsData?.data,
        "name",
        "Unnamed Inpatient Ruleset"
      ),
    [inpatientRulesetsData]
  );

  const medicareAdvantagePayerOptions = useMemo(() => {
    return (
      payers?.data
        ?.filter((p: any) => p.type === "UhcMedicareAdvantage")
        ?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  const handleInputChange = (field: keyof IFormData, value: any) => {
    // MultiSelect returns null when cleared; normalize to [] for array fields
    let normalizedValue =
      (field === "providerIds" || field === "payerIds") && value == null
        ? []
        : value;
    if (
      (field === "effectiveDate" || field === "nextUpdateDate") &&
      normalizedValue instanceof Date
    ) {
      normalizedValue = normalizeCalendarDayToLocalNoon(normalizedValue);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const addRateCalculation = () => {
    if (newRateCalc.ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate Calculation",
        detail:
          newRateCalc.rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }

    const rateCalculation: RateCalculation = {
      id: "", // Will be set by backend
      baseRateFileId: newRateCalc.baseRateFileId || undefined,
      ratePercent: Number(newRateCalc.ratePercent),
      rateType: newRateCalc.rateType,
      sortOrder: formData.rateCalculations.length,
    };

    setFormData((prev) => ({
      ...prev,
      rateCalculations: [...prev.rateCalculations, rateCalculation],
    }));

    // Reset form
    setNewRateCalc({
      baseRateFileId: "",
      ratePercent: 100,
      rateType: "percent",
    });
  };

  const removeRateCalculation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (formData.providerIds.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select at least one provider.",
      });
      return;
    }

    if (formData.payerIds.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select at least one payer.",
      });
      return;
    }

    if (!formData.effectiveDate) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is required.",
      });
      return;
    }

    const effectiveDateIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveDateIso) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is invalid.",
      });
      return;
    }

    if (formData.rateCalculations.length === 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail:
          "At least one rate calculation is required to create a contract fee schedule.",
      });
      return;
    }

    const isMultipleCreation =
      formData.providerIds.length > 1 || formData.payerIds.length > 1;

    if (!isMultipleCreation) {
      const refreshed = await refetchExistingContractPair();
      if (refreshed.error) {
        toastElement?.show({
          severity: "error",
          summary: "Unable to verify",
          detail:
            "We could not check whether a contract already exists for this provider and payer. Try again.",
        });
        return;
      }
      const pairRows = refreshed.data;
      if (Array.isArray(pairRows) && pairRows.length > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Contract already exists",
          detail:
            "A contract already exists for this provider and payer. Open that contract and add a new term instead of creating another contract.",
        });
        return;
      }
    } else {
      const pairs = formData.providerIds.flatMap((pid) =>
        formData.payerIds.map((pyid) => ({ pid, pyid }))
      );
      let conflicts: string[] = [];
      try {
        const lookupResults = await Promise.all(
          pairs.map(({ pid, pyid }) =>
            lookupContractsForPair({
              providerId: pid,
              payerId: pyid,
              ...(accountId ? { accountId } : {}),
            })
              .unwrap()
              .then((rows) => ({ pid, pyid, rows }))
          )
        );
        conflicts = lookupResults
          .filter(({ rows }) => rows.length > 0)
          .map(({ pid, pyid }) => {
            const pn =
              providers?.data?.find((p: any) => p.id === pid)?.name ?? pid;
            const payn =
              payers?.data?.find((p: any) => p.id === pyid)?.name ??
              String(pyid);
            return `${pn} – ${payn}`;
          });
      } catch {
        toastElement?.show({
          severity: "error",
          summary: "Unable to verify",
          detail: "We could not check for existing contracts. Try again.",
        });
        return;
      }
      if (conflicts.length > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Contract already exists",
          detail: `A contract already exists for: ${conflicts.join(
            "; "
          )}. Add a new term on each existing contract instead of creating another.`,
        });
        return;
      }
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      // Filter ruleset IDs to only include ones that still exist
      const availableMpdrIds = mpdrRulesets?.data?.map((r: any) => r.id) || [];
      const availableCaseRateIds =
        caseRateRulesets?.data?.map((r: any) => r.id) || [];

      const validMpdrIds = formData.mpdrRulesetIds.filter((id) =>
        availableMpdrIds.includes(id)
      );
      const validCaseRateIds = formData.caseRateRulesetIds.filter((id) =>
        availableCaseRateIds.includes(id)
      );

      const availableExplodingIds =
        explodingFeeSchedulesData?.data?.map((r: any) => r.id) || [];
      const availablePackageIds =
        packageRulesetsData?.data?.map((r: any) => r.id) || [];
      const availableInpatientIds =
        inpatientRulesetsData?.data?.map((r: any) => r.id) || [];

      const validExplodingIds = formData.explodingFeeScheduleRulesetIds.filter(
        (id) => availableExplodingIds.includes(id)
      );
      const validPackageIds = formData.packageRulesetIds.filter((id) =>
        availablePackageIds.includes(id)
      );
      const validInpatientIds = formData.inpatientRulesetIds.filter((id) =>
        availableInpatientIds.includes(id)
      );

      // Check if we need to use bulk creation (same as isMultipleCreation above)
      const totalContracts =
        formData.providerIds.length * formData.payerIds.length;

      if (isMultipleCreation) {
        // Use bulk creation
        const bulkData = {
          providerIds: formData.providerIds,
          payerIds: formData.payerIds,
          effectiveDate: effectiveDateIso,
          nextUpdateDate: localCalendarDateToIsoDateString(
            formData.nextUpdateDate
          ),
          rateCalculations: formData.rateCalculations.map((calc, index) => ({
            baseRateFileId: calc.baseRateFileId,
            ratePercent: calc.ratePercent,
            rateType: calc.rateType ?? "percent",
            sortOrder: index,
          })),
          mpdrRulesetIds: validMpdrIds.length > 0 ? validMpdrIds : undefined,
          caseRateRulesetIds:
            validCaseRateIds.length > 0 ? validCaseRateIds : undefined,
          explodingFeeScheduleRulesetIds:
            validExplodingIds.length > 0 ? validExplodingIds : undefined,
          packageRulesetIds:
            validPackageIds.length > 0 ? validPackageIds : undefined,
          inpatientRulesetIds:
            validInpatientIds.length > 0 ? validInpatientIds : undefined,
          medicareAdvantagePayerIds:
            formData.medicareAdvantagePayerIds.length > 0
              ? formData.medicareAdvantagePayerIds
              : undefined,
          copyFromContractId:
            copyFromId && copyCarveouts ? copyFromId : undefined,
          carveoutBaseRateFileIds:
            pendingCarveoutBaseRateFileIds.length > 0
              ? pendingCarveoutBaseRateFileIds
              : undefined,
          notes: formData.notes || undefined,
        };

        toastElement?.show({
          severity: "info",
          summary: "Creating Contracts",
          detail: `Creating ${totalContracts} contract(s)...`,
        });

        const result = await createBulkContract(bulkData).unwrap();

        toastElement?.show({
          severity: "success",
          summary: "Contracts Created",
          detail:
            pendingCarveoutBaseRateFileIds.length > 0
              ? `${result.message} with ${pendingCarveoutBaseRateFileIds.length} carveout file(s) attached.`
              : result.message,
        });

        // Navigate to contracts list - handle both hospital and admin contexts
        if (hospitalId) {
          navigate(`/hospitals/view/${hospitalId}/contracts/library`);
        } else {
          navigate("/payer-rules?tab=6");
        }
      } else {
        // Single contract creation
        const contractData = {
          providerId: formData.providerIds[0],
          payerId: formData.payerIds[0],
          effectiveDate: effectiveDateIso,
          nextUpdateDate: localCalendarDateToIsoDateString(
            formData.nextUpdateDate
          ),
          rateCalculations: formData.rateCalculations.map((calc, index) => ({
            baseRateFileId: calc.baseRateFileId,
            ratePercent: calc.ratePercent,
            rateType: calc.rateType ?? "percent",
            sortOrder: index,
          })),
          mpdrRulesetIds: validMpdrIds.length > 0 ? validMpdrIds : undefined,
          caseRateRulesetIds:
            validCaseRateIds.length > 0 ? validCaseRateIds : undefined,
          explodingFeeScheduleRulesetIds:
            validExplodingIds.length > 0 ? validExplodingIds : undefined,
          packageRulesetIds:
            validPackageIds.length > 0 ? validPackageIds : undefined,
          inpatientRulesetIds:
            validInpatientIds.length > 0 ? validInpatientIds : undefined,
          medicareAdvantagePayerIds:
            formData.medicareAdvantagePayerIds.length > 0
              ? formData.medicareAdvantagePayerIds
              : undefined,
          copyFromContractId:
            copyFromId && copyCarveouts ? copyFromId : undefined,
          carveoutBaseRateFileIds:
            pendingCarveoutBaseRateFileIds.length > 0
              ? pendingCarveoutBaseRateFileIds
              : undefined,
          notes: formData.notes || undefined,
        };

        const result = await createContract(contractData as any).unwrap();

        // Upload attachment files if any were selected
        if (formData.attachmentFiles.length > 0) {
          try {
            toastElement?.show({
              severity: "info",
              summary: "Uploading Attachments",
              detail: `Uploading ${formData.attachmentFiles.length} attachment file(s)...`,
            });

            const attachmentPromises = formData.attachmentFiles.map((file) =>
              uploadAttachment({
                contractId: result.id,
                file,
              }).unwrap()
            );

            await Promise.all(attachmentPromises);
          } catch (attachmentError) {
            toastElement?.show({
              severity: "warn",
              summary: "Contract Created",
              detail:
                "Contract was created but some attachment files failed to upload. You can upload them from the edit page.",
            });
          }
        }

        if (formData.attachmentFiles.length === 0) {
          toastElement?.show({
            severity: "success",
            summary: "Contract Created",
            detail: "Contract fee schedule has been created successfully.",
          });
        } else {
          toastElement?.show({
            severity: "success",
            summary: "Contract Created",
            detail: "Contract fee schedule and files uploaded successfully.",
          });
        }

        // Navigate to contract view - handle both hospital and admin contexts
        if (hospitalId) {
          navigate(`/hospitals/view/${hospitalId}/contracts/library`);
        } else {
          navigate(`/payer-rules/contracts/${result.id}`);
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to create contract fee schedule.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  const rateCalculationBodyTemplate = (
    rowData: RateCalculation,
    options: any
  ) => {
    const rateType = getRateTypeForDisplay(rowData);
    return (
      <div className="flex justify-content-between align-items-center">
        <div>
          <div className="font-medium">
            {rowData.baseRateFileId
              ? baseRateFileOptions.find(
                  (f) => f.value === rowData.baseRateFileId
                )?.label || "Base Rate File"
              : "No base rate file"}
          </div>
          <div className="text-sm">
            {rateType === "unit"
              ? `Price per Unit: ${rowData.ratePercent / 100}`
              : `Rate: ${rowData.ratePercent}%`}
          </div>
        </div>
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text"
          onClick={() => removeRateCalculation(options.rowIndex)}
        />
      </div>
    );
  };

  const attachmentFileActionsTemplate = (rowData: File, options: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() => {
        const newFiles = formData.attachmentFiles.filter(
          (_, index) => index !== options.rowIndex
        );
        handleInputChange("attachmentFiles", newFiles);
      }}
      tooltip="Remove file"
    />
  );

  const pendingCarveoutDisplayNameBody = (fileId: string) =>
    baseRateFiles?.data?.find((f) => f.id === fileId)?.displayName ?? fileId;

  const pendingCarveoutActionsBody = (fileId: string) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() =>
        setPendingCarveoutBaseRateFileIds((prev) =>
          prev.filter((id) => id !== fileId)
        )
      }
    />
  );

  const firstExistingContractId = existingContractsForPair[0]?.id;
  let openExistingContractPath: string | undefined;
  if (firstExistingContractId) {
    if (hospitalOrAccountForContractLinks) {
      openExistingContractPath = `/hospitals/view/${hospitalOrAccountForContractLinks}/contracts/${firstExistingContractId}`;
    } else {
      openExistingContractPath = `/payer-rules/contracts/${firstExistingContractId}`;
    }
  }

  const hasBlockingDuplicateForSinglePair =
    formData.providerIds.length === 1 &&
    formData.payerIds.length === 1 &&
    existingContractsForPair.length > 0;

  return (
    <div className="card">
      {!hospitalId && (
        <div className="mb-3">
          <Button
            label="Return to Contract Fee Schedules"
            icon="pi pi-arrow-left"
            className="p-button-text p-button-plain text-600"
            onClick={() => navigate("/payer-rules?tab=6")}
          />
        </div>
      )}
      <div className="mb-4">
        <h2>Create Contract Fee Schedule</h2>
      </div>

      {/* Copy notification banner */}
      {copyFromId && sourceContract?.data && (
        <div className="mb-4 p-3 border-1 border-300 border-round bg-blue-50">
          <div className="flex align-items-center">
            <i className="pi pi-copy text-blue-600 mr-2 text-2xl" />
            <div>
              <div className="text-blue-800 font-bold">
                Copying from existing contract
              </div>
              <div className="text-blue-700 text-sm mt-1">
                This new contract will be created with settings copied from{" "}
                {sourceContract.data.provider?.name} -{" "}
                {sourceContract.data.payer?.name}
                {sourceContract.data.carveOutFiles &&
                  sourceContract.data.carveOutFiles.length > 0 &&
                  copyCarveouts &&
                  ` including ${sourceContract.data.carveOutFiles.length} carveout file(s)`}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid">
        {/* Basic Information */}
        <div className="col-12">
          <Card title="Basic Information" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Provider(s) *
                </label>
                <MultiSelect
                  value={formData.providerIds}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerIds", e.value)}
                  placeholder="Select Provider(s)"
                  className="w-full"
                  filter
                  display="chip"
                  showClear
                />
                {formData.providerIds.length > 0 && (
                  <small className="text-600 mt-1 block">
                    {formData.providerIds.length} provider(s) selected
                  </small>
                )}
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Payer(s) *
                </label>
                <MultiSelect
                  value={formData.payerIds}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerIds", e.value)}
                  placeholder="Select Payer(s)"
                  className="w-full"
                  filter
                  display="chip"
                  showClear
                />
                {formData.payerIds.length > 0 && (
                  <small className="text-600 mt-1 block">
                    {formData.payerIds.length} payer(s) selected
                  </small>
                )}
              </div>
              {hasBlockingDuplicateForSinglePair && (
                <div className="col-12">
                  <Message
                    severity="warn"
                    className="w-full"
                    text="A contract already exists for this provider and payer. Add a new term on that contract instead of creating another one."
                  />
                  {openExistingContractPath && (
                    <Button
                      type="button"
                      label="Open existing contract"
                      icon="pi pi-external-link"
                      className="p-button-text mt-2"
                      onClick={() => {
                        if (openExistingContractPath) {
                          navigate(openExistingContractPath);
                        }
                      }}
                    />
                  )}
                </div>
              )}
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Effective Date *
                </label>
                <Calendar
                  value={formData.effectiveDate}
                  onChange={(e) => handleInputChange("effectiveDate", e.value)}
                  placeholder="Select Effective Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Next Update Date
                </label>
                <Calendar
                  value={formData.nextUpdateDate}
                  onChange={(e) => handleInputChange("nextUpdateDate", e.value)}
                  placeholder="Select Next Update Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <div className="col-12">
                <label className="block text-900 font-medium mb-2">Notes</label>
                <InputTextarea
                  value={formData.notes || ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Enter any notes or special instructions for this contract..."
                  rows={4}
                  className="w-full"
                  autoResize
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Medicare Advantage Payers - show when ANY payer is Medicare */}
        {hasMedicarePayer && (
          <div className="col-12">
            <Card title="Medicare Advantage Payers" className="mb-4">
              <p className="mb-3 text-600">
                Select Medicare Advantage payers that should use Medicare
                contract procedures and pricing.
              </p>
              <MultiSelect
                value={formData.medicareAdvantagePayerIds}
                options={medicareAdvantagePayerOptions}
                onChange={(e) =>
                  handleInputChange("medicareAdvantagePayerIds", e.value)
                }
                placeholder="Select Medicare Advantage payers"
                className="w-full"
                display="chip"
                showClear
                filter
              />
              <small className="text-600 mt-2 block">
                These Medicare Advantage payers will be linked only to contracts
                with Original Medicare as the primary payer.
              </small>
            </Card>
          </div>
        )}

        {/* Rate Calculations */}
        <div className="col-12">
          <Card title="Rate Calculations" className="mb-4">
            <div className="mb-4">
              <div className="grid">
                <div className="col-12 md:col-5">
                  <label className="block text-900 font-medium mb-2">
                    Base Rate File
                  </label>
                  <Dropdown
                    value={newRateCalc.baseRateFileId}
                    options={baseRateFileOptions}
                    onChange={(e) =>
                      setNewRateCalc((prev) => ({
                        ...prev,
                        baseRateFileId: e.value,
                      }))
                    }
                    placeholder="Select Base Rate File"
                    className="w-full"
                    filter
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label className="block text-900 font-medium mb-2">
                    Type
                  </label>
                  <Dropdown
                    value={newRateCalc.rateType}
                    options={[
                      { label: "$", value: "unit" },
                      { label: "%", value: "percent" },
                    ]}
                    onChange={(e) => {
                      const newType = (e.value ?? "percent") as RateType;
                      setNewRateCalc((prev) => ({
                        ...prev,
                        rateType: newType,
                        ratePercent: 100,
                      }));
                    }}
                    placeholder="Select type"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-3">
                  <label className="block text-900 font-medium mb-2">
                    {newRateCalc.rateType === "unit"
                      ? "Price per Unit"
                      : "Rate Percentage"}
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      value={
                        newRateCalc.rateType === "unit"
                          ? newRateCalc.ratePercent / 100
                          : newRateCalc.ratePercent
                      }
                      onValueChange={(e) =>
                        setNewRateCalc((prev) => ({
                          ...prev,
                          ratePercent:
                            prev.rateType === "unit"
                              ? (e.value || 0) * 100
                              : e.value || 0,
                        }))
                      }
                      placeholder={
                        newRateCalc.rateType === "unit"
                          ? "Price per Unit"
                          : "Rate %"
                      }
                      className="w-full"
                      mode="decimal"
                      min={0}
                      max={100000}
                      minFractionDigits={0}
                      maxFractionDigits={
                        newRateCalc.rateType === "unit" ? 4 : 2
                      }
                    />
                    {newRateCalc.rateType !== "unit" && (
                      <span className="p-inputgroup-addon">%</span>
                    )}
                  </div>
                </div>
                <div className="col-12 md:col-2 flex align-items-end">
                  <Button
                    label="Add Rate"
                    icon="pi pi-plus"
                    onClick={addRateCalculation}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <DataTable
              value={formData.rateCalculations}
              emptyMessage="No rate calculations added yet."
              className="mt-3"
            >
              <Column
                body={rateCalculationBodyTemplate}
                header="Rate Calculations"
              />
            </DataTable>
          </Card>
        </div>

        {/* Carve-Outs */}
        <div className="col-12">
          <Card title="Carve-Outs" className="mb-4">
            <p className="mb-3 text-600">
              Attach carveout CSVs from your library.
            </p>

            {/* Show source contract carveouts when copying */}
            {copyFromId &&
              sourceContract?.data?.carveOutFiles &&
              sourceContract.data.carveOutFiles.length > 0 && (
                <div className="mb-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <div className="flex align-items-start mb-2">
                    <input
                      type="checkbox"
                      id="copyCarveouts"
                      checked={copyCarveouts}
                      onChange={(e) => setCopyCarveouts(e.target.checked)}
                      className="mr-2 mt-1"
                    />
                    <label
                      htmlFor="copyCarveouts"
                      className="flex-grow-1 cursor-pointer"
                    >
                      <span className="text-blue-800 font-medium">
                        Copy carveout attachment(s) from the source contract
                      </span>
                      {copyCarveouts && (
                        <ul className="m-0 pl-3 mt-2">
                          {sourceContract.data.carveOutFiles.map(
                            (file: ICarveout) => (
                              <li key={file.id} className="text-blue-700">
                                {file.fileName}
                                {file.baseRateFileId
                                  ? " (library link)"
                                  : " (legacy upload)"}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </label>
                  </div>
                  {!copyCarveouts && (
                    <div className="text-blue-700 text-sm mt-2 ml-4">
                      <i className="pi pi-exclamation-triangle mr-1" />
                      Carveouts will not be copied from the source contract.
                    </div>
                  )}
                </div>
              )}

            <div className="grid">
              <div className="col-12 md:col-8">
                <label className="block text-900 font-medium mb-2">
                  Attach carveout file from library
                </label>
                <Dropdown
                  value={selectedCarveoutBaseRateFileId}
                  options={carveoutBaseRateFileOptions}
                  onChange={(e) => setSelectedCarveoutBaseRateFileId(e.value)}
                  placeholder={
                    carveoutBaseRateFileOptions.length === 0
                      ? "No carveout files available (upload in Base Rate Files)"
                      : "Select carveout file"
                  }
                  className="w-full"
                  filter
                  showClear
                  disabled={carveoutBaseRateFileOptions.length === 0}
                />
              </div>
              <div className="col-12 md:col-4 flex align-items-end">
                <Button
                  type="button"
                  label="Attach"
                  icon="pi pi-plus"
                  className="w-full"
                  disabled={!selectedCarveoutBaseRateFileId}
                  onClick={() => {
                    if (
                      !selectedCarveoutBaseRateFileId ||
                      pendingCarveoutBaseRateFileIds.includes(
                        selectedCarveoutBaseRateFileId
                      )
                    ) {
                      return;
                    }
                    setPendingCarveoutBaseRateFileIds((prev) => [
                      ...prev,
                      selectedCarveoutBaseRateFileId,
                    ]);
                    setSelectedCarveoutBaseRateFileId(null);
                  }}
                />
              </div>
            </div>

            {pendingCarveoutBaseRateFileIds.length > 0 && (
              <div className="mt-4">
                <DataTable value={pendingCarveoutBaseRateFileIds}>
                  <Column
                    header="Display name"
                    body={pendingCarveoutDisplayNameBody}
                  />
                  <Column header="Actions" body={pendingCarveoutActionsBody} />
                </DataTable>
              </div>
            )}
          </Card>
        </div>
        {/* Rule Set Selection */}
        <div className="col-12">
          <Card title="Rule Set Selection" className="mb-4">
            {/* MPDR Rule Sets */}
            <div className="mb-4">
              <label className="block text-900 font-medium mb-2">
                Multiple Procedure Discount Rule Sets
              </label>
              <MultiSelect
                value={formData.mpdrRulesetIds.filter((id) =>
                  mpdrRulesetOptions.some((g) =>
                    g.items.some((opt) => opt.value === id)
                  )
                )}
                options={mpdrRulesetOptions}
                optionLabel="label"
                optionGroupLabel="label"
                optionGroupChildren="items"
                optionGroupTemplate={rulesetGroupTemplate}
                onChange={(e) => handleInputChange("mpdrRulesetIds", e.value)}
                placeholder="Select MPDR rule sets"
                className="w-full mb-2"
                display="chip"
                showClear
                filter
                filterBy="searchText"
                filterPlaceholder="Search by name, provider, or payer"
                showSelectAll={false}
              />

              {/* Show existing MPDR rulesets if single provider/payer selected */}
              {existingMpdrRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <h6 className="mt-0 mb-2 text-blue-800">
                    <i className="pi pi-info-circle mr-2" />
                    Existing MPDR Rule Sets for {
                      selectedProviders[0]?.name
                    } - {selectedPayers[0]?.name}
                  </h6>
                  <p className="text-sm text-blue-700 mb-3">
                    These rule sets will be automatically linked to the new
                    contract:
                  </p>
                  {existingMpdrRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingMpdrRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/mppr/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed MPDR Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        Rank 2: {ruleset.discountPercentForRank2}%, Rank 3:{" "}
                        {ruleset.discountPercentForRank3}%, Rank 4:{" "}
                        {ruleset.discountPercentForRank4}%, Rank 5+:{" "}
                        {ruleset.discountPercentForRank5Plus}%
                      </div>
                      <div className="text-sm text-600">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <span>
                            CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </span>
                        ) : (
                          "No CPT codes configured"
                        )}
                        {ruleset.allowOnDuplicateCptCodes &&
                          " • Allows duplicate codes"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-top pt-4">
              {/* Case Rate Rule Sets */}
              <label className="block text-900 font-medium mb-2">
                Case Rate Rule Sets
              </label>
              <MultiSelect
                value={formData.caseRateRulesetIds.filter((id) =>
                  caseRateRulesetOptions.some((g) =>
                    g.items.some((opt) => opt.value === id)
                  )
                )}
                options={caseRateRulesetOptions}
                optionLabel="label"
                optionGroupLabel="label"
                optionGroupChildren="items"
                optionGroupTemplate={rulesetGroupTemplate}
                onChange={(e) =>
                  handleInputChange("caseRateRulesetIds", e.value)
                }
                placeholder="Select case rate rule sets"
                className="w-full mb-2"
                display="chip"
                showClear
                filter
                filterBy="searchText"
                filterPlaceholder="Search by name, provider, or payer"
                showSelectAll={false}
              />

              {/* Show existing case rate rulesets if single provider/payer selected */}
              {existingCaseRateRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                  <h6 className="mt-0 mb-2 text-blue-800">
                    <i className="pi pi-info-circle mr-2" />
                    Existing Case Rate Rule Sets for{" "}
                    {selectedProviders[0]?.name} - {selectedPayers[0]?.name}
                  </h6>
                  <p className="text-sm text-blue-700 mb-3">
                    These rule sets will be automatically linked to the new
                    contract:
                  </p>
                  {existingCaseRateRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingCaseRateRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <div>
                            Included CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 10).join(", ")}
                            {ruleset.cptCodes.length > 10 && (
                              <span>
                                , and {ruleset.cptCodes.length - 10} other CPT
                                codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No included CPT codes</div>
                        )}
                        {ruleset.excludedCptCodes &&
                        ruleset.excludedCptCodes.length > 0 ? (
                          <div>
                            Excluded CPT Codes:{" "}
                            {ruleset.excludedCptCodes.slice(0, 10).join(", ")}
                            {ruleset.excludedCptCodes.length > 10 && (
                              <span>
                                , and {ruleset.excludedCptCodes.length - 10}{" "}
                                other CPT codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No excluded CPT codes</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Inpatient Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Inpatient Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() =>
                      navigate("/payer-rules/inpatient-rulesets/new")
                    }
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.inpatientRulesetIds.filter((id) =>
                inpatientRulesetOptions.some((g) =>
                  g.items.some((opt) => opt.value === id)
                )
              )}
              options={inpatientRulesetOptions}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={rulesetGroupTemplate}
              onChange={(e) =>
                handleInputChange("inpatientRulesetIds", e.value)
              }
              placeholder="Select inpatient rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingInpatientRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Inpatient Rulesets for {
                    selectedProviders[0]?.name
                  } - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets are automatically matched to contracts with
                  this provider + payer combination:
                </p>
                {existingInpatientRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingInpatientRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/inpatient-rulesets/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`
                          );
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.name || "Unnamed Inpatient Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      Type: {ruleset.type?.replace(/_/g, " ") || "Unknown"}
                      {ruleset.drgCodes && ruleset.drgCodes.length > 0 && (
                        <span>
                          {" "}
                          | DRG Codes:{" "}
                          {ruleset.drgCodes.slice(0, 10).join(", ")}
                          {ruleset.drgCodes.length > 10 && (
                            <span>
                              , and {ruleset.drgCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Exploding Fee Schedule Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Exploding Fee Schedule Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() =>
                      navigate("/payer-rules/exploding-fee-schedules/new")
                    }
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.explodingFeeScheduleRulesetIds.filter((id) =>
                explodingRulesetOptions.some((g) =>
                  g.items.some((opt) => opt.value === id)
                )
              )}
              options={explodingRulesetOptions}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={rulesetGroupTemplate}
              onChange={(e) =>
                handleInputChange("explodingFeeScheduleRulesetIds", e.value)
              }
              placeholder="Select exploding fee schedule rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingExplodingFeeSchedules.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Exploding Fee Schedule Rulesets for{" "}
                  {selectedProviders[0]?.name} - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets are automatically matched to contracts with
                  this provider + payer combination:
                </p>
                {existingExplodingFeeSchedules.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingExplodingFeeSchedules.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`
                          );
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName ||
                          "Unnamed Exploding Fee Schedule Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <span>
                          CPT Codes: {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span>
                              , and {ruleset.cptCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      ) : (
                        "No CPT codes configured"
                      )}
                      {ruleset.totalRateInCents != null && (
                        <span>
                          {" "}
                          | Total Rate: $
                          {(ruleset.totalRateInCents / 100).toLocaleString(
                            "en-US",
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Package Rulesets */}
        <div className="col-12">
          <Card
            title={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Package Rulesets</span>
                {!hospitalId && (
                  <Button
                    label="Create New"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() => navigate("/payer-rules/packages/new")}
                  />
                )}
              </div>
            }
            className="mb-4"
          >
            <MultiSelect
              value={formData.packageRulesetIds.filter((id) =>
                packageRulesetOptions.some((g) =>
                  g.items.some((opt) => opt.value === id)
                )
              )}
              options={packageRulesetOptions}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={rulesetGroupTemplate}
              onChange={(e) => handleInputChange("packageRulesetIds", e.value)}
              placeholder="Select package rulesets"
              className="w-full mb-2"
              display="chip"
              showClear
              filter
              filterBy="searchText"
              filterPlaceholder="Search by name, provider, or payer"
              showSelectAll={false}
            />

            {existingPackageRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-blue-50">
                <h6 className="mt-0 mb-2 text-blue-800">
                  <i className="pi pi-info-circle mr-2" />
                  Existing Package Rulesets for {
                    selectedProviders[0]?.name
                  } - {selectedPayers[0]?.name}
                </h6>
                <p className="text-sm text-blue-700 mb-3">
                  These rulesets are automatically matched to contracts with
                  this provider + payer combination:
                </p>
                {existingPackageRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingPackageRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/packages/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/payer-rules/packages/edit/${ruleset.id}`);
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Package Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.triggerCptCodes &&
                      ruleset.triggerCptCodes.length > 0 ? (
                        <span>
                          Trigger:{" "}
                          {ruleset.triggerCptCodes.slice(0, 10).join(", ")}
                          {ruleset.triggerCptCodes.length > 10 && (
                            <span>
                              , and {ruleset.triggerCptCodes.length - 10} more
                            </span>
                          )}
                        </span>
                      ) : (
                        "No trigger codes"
                      )}
                      {ruleset.bundledCptCodes &&
                        ruleset.bundledCptCodes.length > 0 && (
                          <span>
                            {" "}
                            | Bundled:{" "}
                            {ruleset.bundledCptCodes.slice(0, 10).join(", ")}
                            {ruleset.bundledCptCodes.length > 10 && (
                              <span>
                                , and {ruleset.bundledCptCodes.length - 10} more
                              </span>
                            )}
                          </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Contract Attachments */}
        <div className="col-12">
          <Card title="Contract Attachments" className="mb-4">
            <p className="mb-3 text-600">
              Attach files to this contract for reference and download (PDFs,
              Excel, images, etc. up to 50MB each).
            </p>

            {formData.attachmentFiles.length === 0 && (
              <FileUpload
                name="attachments"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip"
                maxFileSize={52428800}
                emptyTemplate={
                  <div className="text-center p-4">
                    <i className="pi pi-cloud-upload text-4xl text-400" />
                    <p className="mt-2 mb-0">
                      Drag and drop files here or click to browse.
                    </p>
                  </div>
                }
                mode="basic"
                chooseOptions={{
                  label: "Select Files",
                  icon: "pi pi-fw pi-plus",
                }}
                onSelect={(e) => {
                  const files = Array.from(e.files);
                  handleInputChange("attachmentFiles", [
                    ...formData.attachmentFiles,
                    ...files,
                  ]);
                }}
              />
            )}

            {/* Display selected attachment files */}
            {formData.attachmentFiles.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    Selected Attachment Files ({formData.attachmentFiles.length}
                    )
                  </h6>
                  <Button
                    label="Add More Files"
                    icon="pi pi-plus"
                    className="p-button-outlined p-button-sm"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.accept =
                        ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip";
                      input.onchange = (e) => {
                        const files = Array.from(
                          (e.target as HTMLInputElement).files || []
                        );
                        if (files.length > 0) {
                          handleInputChange("attachmentFiles", [
                            ...formData.attachmentFiles,
                            ...files,
                          ]);
                        }
                      };
                      input.click();
                    }}
                  />
                </div>
                <DataTable
                  value={formData.attachmentFiles}
                  className="p-datatable-sm"
                  emptyMessage="No attachment files selected."
                >
                  <Column
                    header="File Name"
                    body={(rowData: File) => rowData.name}
                  />
                  <Column
                    field="size"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.size / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    header="Actions"
                    body={attachmentFileActionsTemplate}
                  />
                </DataTable>
              </div>
            )}
          </Card>
        </div>

        {/* Submit Actions */}
        <div className="col-12">
          {formData.rateCalculations.length === 0 && (
            <div className="mb-3 p-3 border-1 border-300 border-round bg-yellow-50">
              <div className="flex align-items-center">
                <i className="pi pi-exclamation-triangle text-yellow-600 mr-2" />
                <span className="text-yellow-800">
                  At least one rate calculation is required to save the contract
                  fee schedule.
                </span>
              </div>
            </div>
          )}
          {formData.providerIds.length > 0 && formData.payerIds.length > 0 && (
            <div className="mb-3 p-3 border-1 border-300 border-round bg-blue-50">
              <div className="flex align-items-center">
                <i className="pi pi-info-circle text-blue-600 mr-2" />
                <span className="text-blue-800">
                  This will create{" "}
                  <strong>
                    {formData.providerIds.length * formData.payerIds.length}
                  </strong>{" "}
                  contract
                  {formData.providerIds.length * formData.payerIds.length > 1
                    ? "s"
                    : ""}{" "}
                  ({formData.providerIds.length} provider
                  {formData.providerIds.length > 1 ? "s" : ""} ×{" "}
                  {formData.payerIds.length} payer
                  {formData.payerIds.length > 1 ? "s" : ""})
                </span>
              </div>
            </div>
          )}
          <div className="flex justify-content-end gap-2">
            <Button
              label={
                formData.providerIds.length * formData.payerIds.length > 1
                  ? `Create ${
                      formData.providerIds.length * formData.payerIds.length
                    } Contracts`
                  : "Save Contract"
              }
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                formData.rateCalculations.length === 0 ||
                hasBlockingDuplicateForSinglePair
              }
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateContractFeeSchedulePage;
